<template>
  <AppLayout>
    <div class="transaction">
      <section id="transaction-products">
        <div class="container">
        
        <h6 style="margin-bottom: 0px;" class="fw-bold">All Tender Submissions</h6>
        
        <!-- Stats Cards Section -->
        <div class="warehouse-inventory-card-wrapper">
          <div class="warehouse-inventory-card">
            <p>New <br>
              Submissions</p>
            <strong>{{ stats.submitted || 0 }}</strong>
          </div>

          <div class="warehouse-inventory-card">
            <p>Under <br>
              Review</p>
            <strong class="green">{{ stats.underReview || 0 }}</strong>
          </div>

          <div class="warehouse-inventory-card">
            <p>Shortlisted <br>
              Applications</p>
            <strong class="green">{{ stats.shortlisted || 0 }}</strong>
          </div>

          <div class="warehouse-inventory-card">
            <p>Total <br>
              Submissions</p>
            <strong>{{ stats.total || 0 }}</strong>
          </div>

          <div class="warehouse-inventory-card">
            <p>Rejected <br>
              Applications</p>
            <strong class="red">{{ stats.rejected || 0 }}</strong>
          </div>

          <div class="warehouse-inventory-card">
            <p>Awarded <br>
              Contracts</p>
            <strong class="green">{{ stats.awarded || 0 }}</strong>
          </div>
        </div>

        <!-- Filter Section -->
        <div class="filter-search" style="margin-top: 0px;padding-left: 0px;">
          <div class="filter-search-body">
            <div class="row">
              <div class="col-md-4">
                <div class="form-group">
                  <label for="search">Search Submissions</label>
                  <input type="text" class="form-control" v-model="searchParams.search" @input="debouncedSearch" placeholder="Search by company name, tender title..." />
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label for="status">Status</label>
                  <select class="full" v-model="searchParams.status" @change="fetchSubmissions">
                    <option value="">All Status</option>
                    <option value="SUBMITTED">Submitted</option>
                    <option value="UNDER_REVIEW">Under Review</option>
                    <option value="SHORTLISTED">Shortlisted</option>
                    <option value="REJECTED">Rejected</option>
                    <option value="AWARDED">Awarded</option>
                  </select>
                </div>
              </div>
              <div class="col-md-3">
                <div class="form-group">
                  <label for="tender">Tender</label>
                  <select class="full" v-model="searchParams.tenderId" @change="fetchSubmissions">
                    <option value="">All Tenders</option>
                    <option v-for="tender in tenders" :key="tender.id" :value="tender.id">
                      {{ tender.referenceNumber }} - {{ tender.title }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-md-2 align-self-end">
                <div class="form-group">
                  <button class="btn btn-primary small" style="margin-top: 32px;" type="button" @click="fetchSubmissions">Search</button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Submissions Table -->
        <div class="transaction-products-list" style="margin-top: 0px;">
          <!-- Skeleton loader for submissions table -->
          <SkeletonLoader v-if="loading" type="table" :rows="8" :columns="10" />
          
          <table v-else id="submissionsTable" ref="submissionsTable" class="table table-hover">
            <thead style="background-color: #f8f9fa; border-bottom: 2px solid #dee2e6;">
              <tr>
                <th style="padding: 15px 11px; font-weight: 600; font-size: 11px; color: #495057; text-align: left; border-bottom: none;">No.</th>
                <th style="padding: 15px 11px; font-weight: 600; font-size: 11px; color: #495057; text-align: left; border-bottom: none;">Company</th>
                <th style="padding: 15px 11px; font-weight: 600; font-size: 11px; color: #495057; text-align: left; border-bottom: none;">Tender</th>
                <th style="padding: 15px 11px; font-weight: 600; font-size: 11px; color: #495057; text-align: left; border-bottom: none;">Submitted</th>
                <th style="padding: 15px 11px; font-weight: 600; font-size: 11px; color: #495057; text-align: left; border-bottom: none;">Value</th>
                <th style="padding: 15px 11px; font-weight: 600; font-size: 11px; color: #495057; text-align: left; border-bottom: none;">Contract Start</th>
                <th style="padding: 15px 11px; font-weight: 600; font-size: 11px; color: #495057; text-align: left; border-bottom: none;">Contract End</th>
                <th style="padding: 15px 11px; font-weight: 600; font-size: 11px; color: #495057; text-align: left; border-bottom: none;">Contract Status</th>
                <th style="padding: 15px 11px; font-weight: 600; font-size: 11px; color: #495057; text-align: left; border-bottom: none;">Status</th>
                <th style="padding: 15px 11px; font-weight: 600; font-size: 11px; color: #495057; text-align: left; border-bottom: none;">Actions</th>
              </tr>
            </thead>
            <tbody v-if="!loading && submissions.length > 0">
              <tr v-for="(submission, index) in submissions" :key="submission.id" @click="viewSubmission(submission)" style="cursor: pointer;">
                <td>{{ index + 1 }}</td>
                <td>
                  <div>
                    <p>{{ submission.companyName }}</p>
                    <small>{{ submission.contactPerson }}</small>
                  </div>
                </td>
                <td>
                  <div>
                    <p>{{ submission.tender?.title }}</p>
                    <small>{{  submission.tender?.referenceNumber}}</small>
                  </div>
                </td>
                <td>{{ formatDate(submission.submittedAt) }}</td>
                <td>{{ submission.proposedValue ? formatCurrency(submission.proposedValue, submission.proposedCurrency || submission.tender?.currency) : 'N/A' }}</td>
                <td>{{ submission.tenderContract ? formatDate(submission.tenderContract.startDate) : '—' }}</td>
                <td>{{ submission.tenderContract ? formatDate(submission.tenderContract.endDate) : '—' }}</td>
                <td>
                  <span v-if="submission.tenderContract" :class="`badge badge-${getContractStatusClass(submission.tenderContract)}`">
                    {{ getContractStatusLabel(submission.tenderContract) }}
                  </span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td>
                  <span :class="`badge badge-${getStatusClass(submission.status)}`">
                    {{ formatStatus(submission.status) }}
                  </span>
                </td>
                <td>
                  <div class="btn-group" role="group">
                    <button
                      @click.stop="viewSubmission(submission)"
                      :data-id="submission.id"
                      class="btn btn-sm btn-outline-primary submission-view"
                      title="View Submission"
                    >
                      <i class="fas fa-eye"></i>
                    </button>
                    <button
                      @click.stop="updateStatus(submission.id, 'UNDER_REVIEW')"
                      v-if="submission.status === 'SUBMITTED'"
                      :data-id="submission.id"
                      class="btn btn-sm btn-outline-info submission-start-review"
                      title="Start Review"
                    >
                      <i class="fas fa-search"></i>
                    </button>
                    <button
                      @click.stop="updateStatus(submission.id, 'SHORTLISTED')"
                      v-if="submission.status === 'UNDER_REVIEW'"
                      :data-id="submission.id"
                      class="btn btn-sm btn-outline-success submission-shortlist"
                      title="Shortlist"
                    >
                      <i class="fas fa-star"></i>
                    </button>
                    <button
                      @click.stop="updateStatus(submission.id, 'REJECTED')"
                      v-if="['SUBMITTED', 'UNDER_REVIEW'].includes(submission.status)"
                      :data-id="submission.id"
                      class="btn btn-sm btn-outline-danger submission-reject"
                      title="Reject"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                    <button
                      @click.stop="openAwardModal(submission)"
                      v-if="submission.status === 'SHORTLISTED'"
                      :data-id="submission.id"
                      class="btn btn-sm btn-outline-success submission-award"
                      title="Award Contract"
                    >
                      <i class="fas fa-trophy"></i>
                    </button>
                    <button
                      @click.stop="openContractView(submission)"
                      v-if="submission.status === 'AWARDED' && submission.tenderContract"
                      :data-id="submission.id"
                      class="btn btn-sm btn-success submission-contract"
                      title="View Contract / Generate PO"
                    >
                      <i class="fas fa-file-contract"></i>
                    </button>
                    <button
                      @click.stop="allowResubmission(submission)"
                      v-if="!submission.allowResubmission"
                      :data-id="submission.id"
                      class="btn btn-sm btn-outline-secondary submission-resubmission"
                      title="Allow one manual resubmission"
                    >
                      <i class="fas fa-redo"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- Empty State -->
          <div v-if="!loading && submissions.length === 0" class="empty-state text-center py-5">
            <i class="fas fa-inbox fa-3x text-muted mb-3"></i>
            <h5 class="text-muted">No submissions found</h5>
            <p class="text-muted">No applications match your current filters</p>
          </div>

        </div>
      </div>
    </section>
  </div>

  <!-- Award Modal: Normal Award vs Contract Award -->
  <div v-if="showAwardModal" class="modal-overlay" @click.self="closeAwardModal">
    <div class="modal-content" style="max-width:520px;">
      <div class="modal-header">
        <h2 style="font-size:16px;">Award — {{ awardingSubmission?.companyName }}</h2>
        <button class="close-btn" @click="closeAwardModal">&times;</button>
      </div>
      <div class="modal-body">
        <div class="mb-3" style="background:#f8fafc;border-radius:8px;padding:11px 16px;">
          <label class="form-label" style="font-size:11px;font-weight:600;">Award Type *</label>
          <div class="d-flex gap-3">
            <label class="d-flex align-items-center gap-2 cursor-pointer">
              <input type="radio" v-model="awardForm.awardType" value="normal" />
              <span>Normal Award</span>
            </label>
            <label class="d-flex align-items-center gap-2 cursor-pointer">
              <input type="radio" v-model="awardForm.awardType" value="contract" />
              <span>Contract (with dates)</span>
            </label>
          </div>
          <p class="text-muted small mt-1 mb-0">
            <span v-if="awardForm.awardType === 'normal'">One-time award. You'll add a PO directly.</span>
            <span v-else>Generate POs from the contract; spend is tracked against contract value.</span>
          </p>
        </div>
        <template v-if="awardForm.awardType === 'contract'">
          <div class="mb-3" style="background:#f0f7ff;border-radius:8px;padding:11px 16px;">
            <div class="mb-3">
              <label class="form-label" style="font-size:11px;">Contract Value *</label>
              <input type="number" class="form-control form-control-sm" v-model="awardForm.contractValue" placeholder="e.g. 50000" min="0" />
            </div>
            <div class="row g-2 mb-3">
              <div class="col-6">
                <label class="form-label" style="font-size:11px;">Start Date *</label>
                <input type="date" class="form-control form-control-sm" v-model="awardForm.startDate" />
              </div>
              <div class="col-6">
                <label class="form-label" style="font-size:11px;">End Date *</label>
                <input type="date" class="form-control form-control-sm" v-model="awardForm.endDate" />
              </div>
            </div>
            <div class="mb-0">
              <label class="form-label" style="font-size:11px;">Contract Notes</label>
              <textarea class="form-control form-control-sm" rows="2" v-model="awardForm.contractNotes" placeholder="Optional..."></textarea>
            </div>
          </div>
        </template>
        <div class="mb-3" style="background:#f8fafc;border-radius:8px;padding:11px 16px;">
          <label class="form-label" style="font-size:11px;">Evaluation Notes</label>
          <textarea class="form-control form-control-sm" rows="2" v-model="awardForm.evaluationNotes" placeholder="Optional notes..."></textarea>
        </div>
      </div>
      <div class="modal-footer" style="display:flex;justify-content:flex-end;gap:8px;padding:11px 24px;border-top:1px solid #e5e7eb;">
        <button class="btn btn-sm btn-style-2" @click="closeAwardModal">Cancel</button>
        <button class="btn btn-sm btn-success" :disabled="awarding || !canSubmitAward" @click="submitAward">
          <span v-if="awarding"><i class="fas fa-spinner fa-spin me-1"></i>Awarding...</span>
          <span v-else><i class="fas fa-trophy me-1"></i>{{ awardForm.awardType === 'contract' ? 'Award Contract' : 'Award' }}</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Contract View Modal -->
  <div v-if="showContractModal" class="modal-overlay" @click.self="closeContractModal">
    <div class="modal-content contract-details-modal">
      <div class="modal-header">
        <h4 class="modal-title">Contract Details</h4>
        <button class="close-btn" @click="closeContractModal" aria-label="Close">&times;</button>
      </div>
      <div class="modal-body">
        <div v-if="loadingContract" class="contract-loading">
          <i class="fas fa-spinner fa-spin"></i>
          <span>Loading contract...</span>
        </div>
        <div v-else-if="currentContract" class="contract-details-body">
          <div class="contract-info-card">
            <div class="contract-info-grid">
              <div class="contract-info-item">
                <span class="contract-label">Tender</span>
                <span class="contract-value">{{ currentContract.tender?.title }}</span>
              </div>
              <div class="contract-info-item">
                <span class="contract-label">Supplier</span>
                <span class="contract-value">{{ currentContract.supplier?.name || currentContract.submission?.companyName }}</span>
              </div>
              <div class="contract-info-item">
                <span class="contract-label">Contract Value</span>
                <span class="contract-value contract-value-emphasis">{{ formatCurrency(currentContract.contractValue, currentContract.tender?.currency || currentContract.submission?.proposedCurrency) }}</span>
              </div>
              <div class="contract-info-item">
                <span class="contract-label">Status</span>
                <span :class="['contract-status-badge', currentContract.status?.toLowerCase()]">{{ currentContract.status }}</span>
              </div>
              <div class="contract-info-item">
                <span class="contract-label">Start Date</span>
                <span class="contract-value">{{ formatDate(currentContract.startDate) }}</span>
              </div>
              <div class="contract-info-item">
                <span class="contract-label">End Date</span>
                <span class="contract-value">{{ formatDate(currentContract.endDate) }}</span>
              </div>
            </div>
          </div>
          <div v-if="currentContract.notes" class="contract-notes">
            <span class="contract-label">Notes</span>
            <p class="contract-notes-text">{{ currentContract.notes }}</p>
          </div>
          <div v-if="currentContract.renewals?.length" class="contract-section">
            <h3 class="contract-section-title">Renewal History</h3>
            <table class="contract-table">
              <thead><tr><th>#</th><th>Start</th><th>End</th><th>Value</th><th>Status</th></tr></thead>
              <tbody>
                <tr v-for="r in currentContract.renewals" :key="r.id">
                  <td>#{{ r.id }}</td>
                  <td>{{ formatDate(r.startDate) }}</td>
                  <td>{{ formatDate(r.endDate) }}</td>
                   <td>{{ formatCurrency(r.contractValue, currentContract.tender?.currency || currentContract.submission?.proposedCurrency) }}</td>
                  <td><span class="contract-badge-sm">{{ r.status }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="contract-section">
            <h4 class="contract-section-title">Purchase Orders ({{ currentContract.purchases?.length || 0 }})</h4>
            <div v-if="!currentContract.purchases?.length" class="contract-empty">No POs generated yet.</div>
            <table v-else class="contract-table">
              <thead><tr><th>PO #</th><th>Status</th><th>Amount</th><th>Date</th></tr></thead>
              <tbody>
                <tr v-for="po in sortedContractPurchases" :key="po.id">
                  <td>{{ po.purchaseNo }}</td>
                  <td><span :class="['contract-badge-sm', 'po-status-' + (po.status || '').toLowerCase()]">{{ po.status }}</span></td>
                   <td>{{ po.amount ? formatCurrency(po.amount, currentContract.tender?.currency || currentContract.submission?.proposedCurrency) : '—' }}</td>
                  <td>{{ formatDate(po.createdAt) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else class="contract-empty-state">
          <p class="contract-empty-message">No contract found for this submission.</p>
          <span v-if="!loadingContract" class="contract-empty-hint">This may be a Normal Award (one-time). Generate a PO from the Add Purchase page.</span>
        </div>
      </div>
      <div class="contract-modal-footer">
        <button class="btn btn-sm btn-style-2" @click="closeContractModal"></button>
        <div class="contract-footer-actions">
          <template v-if="currentContract && currentContract.status !== 'TERMINATED'">
            <button class="btn btn-sm btn-style-2" @click="openExtendModal">
              <i class="fas fa-calendar-plus me-1"></i>Extend
            </button>
            <button class="btn btn-sm btn-style-2" @click="openRenewModal">
              <i class="fas fa-sync-alt me-1"></i>Renew
            </button>
          </template>
          <button v-if="currentContract && currentContract.status === 'ACTIVE'" class="btn btn-sm btn-primary" @click="generatePo">
            <i class="fas fa-file-invoice me-1"></i>Generate PO
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Extend Contract Modal -->
  <div v-if="showExtendModal" class="modal-overlay" @click.self="closeExtendModal">
    <div class="modal-content" style="max-width:440px;">
      <div class="modal-header">
        <h4 style="font-size:16px;" class="fw-bold">Extend Contract</h4>
        <button class="close-btn" @click="closeExtendModal">&times;</button>
      </div>
      <div class="modal-body">
        <p style="font-size:11px;color:#666;margin-bottom:11px;">
          Current end date: <strong>{{ currentContract ? formatDate(currentContract.endDate) : '' }}</strong>.
          Choose a new end date beyond the current one.
        </p>
        <div class="mb-3">
          <label style="font-size:11px;">New End Date <span style="color:red;">*</span></label>
          <input type="date" v-model="extendForm.endDate" class="form-control form-control-sm" :min="minExtendDate" />
        </div>
        <div class="mb-3">
          <label style="font-size:11px;">Updated Contract Value (optional)</label>
          <input type="number" v-model="extendForm.contractValue" class="form-control form-control-sm" placeholder="Leave blank to keep current" min="0" step="0.01"/>
        </div>
        <div class="mb-3">
          <label style="font-size:11px;">Notes (optional)</label>
          <textarea v-model="extendForm.notes" class="form-control form-control-sm" rows="2" placeholder="Reason for extension..."></textarea>
        </div>
      </div>
      <div class="modal-footer" style="display:flex;justify-content:flex-end;gap:8px;padding:11px 24px;border-top:1px solid #e5e7eb;">
        <button class="btn btn-sm btn-style-2" @click="closeExtendModal">Cancel</button>
        <button class="btn btn-sm btn-warning" :disabled="savingExtend" @click="submitExtend">
          <span v-if="savingExtend"><i class="fas fa-spinner fa-spin me-1"></i>Saving...</span>
          <span v-else><i class="fas fa-calendar-plus me-1"></i>Extend</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Renew Contract Modal -->
  <div v-if="showRenewModal" class="modal-overlay" @click.self="closeRenewModal">
    <div class="modal-content" style="max-width:440px;">
      <div class="modal-header">
        <h4 style="font-size:16px;" class="fw-bold"> Renew Contract </h4>
        <button class="close-btn" @click="closeRenewModal">&times;</button>
      </div>
      <div class="modal-body">
        <p style="font-size:11px;color:#666;margin-bottom:11px;">
          A new contract will be created. The current contract will be marked as <strong>RENEWED</strong>.
        </p>
        <div class="row g-2">
          <div class="col-6">
            <label style="font-size:11px;">New Start Date <span style="color:red;">*</span></label>
            <input type="date" v-model="renewForm.startDate" class="form-control form-control-sm"/>
          </div>
          <div class="col-6">
            <label style="font-size:11px;">New End Date <span style="color:red;">*</span></label>
            <input type="date" v-model="renewForm.endDate" class="form-control form-control-sm"/>
          </div>
        </div>
        <div class="mt-3">
          <label style="font-size:11px;">Contract Value (optional)</label>
          <input type="number" v-model="renewForm.contractValue" class="form-control form-control-sm" placeholder="Leave blank to carry over current value" min="0" step="0.01"/>
        </div>
        <div class="mt-3">
          <label style="font-size:11px;">Renewal Notes (optional)</label>
          <textarea v-model="renewForm.renewalNotes" class="form-control form-control-sm" rows="2" placeholder="Reason for renewal..."></textarea>
        </div>
      </div>
      <div class="modal-footer" style="display:flex;justify-content:flex-end;gap:8px;padding:11px 24px;border-top:1px solid #e5e7eb;">
        <button class="btn btn-sm btn-style-2" @click="closeRenewModal">Cancel</button>
        <button class="btn btn-sm btn-info" :disabled="savingRenew" @click="submitRenew">
          <span v-if="savingRenew"><i class="fas fa-spinner fa-spin me-1"></i>Saving...</span>
          <span v-else><i class="fas fa-sync-alt me-1"></i>Renew</span>
        </button>
      </div>
    </div>
  </div>
</AppLayout>
</template>

<script>
/* eslint-disable */
import { ref, reactive, onMounted, computed, watch, onBeforeUnmount } from 'vue'
import $ from 'jquery'
import { useRouter, useRoute } from 'vue-router'
import AppLayout from '../../components/AppLayout.vue'
import SkeletonLoader from '../../components/SkeletonLoader'
import { tenderApi, API_BASE_URL } from '@/api/tender'
import Swal from 'sweetalert2'

// Simple utility functions
const showToast = (message, type = 'info') => {
  console.log(`${type.toUpperCase()}: ${message}`)
}

const debounce = (func, delay) => {
  let timeoutId
  return function (...args) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func.apply(this, args), delay)
  }
}

export default {
  name: 'AllTenderSubmissions',
  components: {
    AppLayout,
    SkeletonLoader
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const loading = ref(false)
    const selectedSubmission = ref(null)
    
    const stats = reactive({
      total: 0,
      submitted: 0,
      underReview: 0,
      shortlisted: 0,
      rejected: 0,
      awarded: 0,
    })
    
    const searchParams = reactive({
      search: '',
      status: '',
      tenderId: '',
      page: 1,
      limit: 1000,
    })
    
    const submissions = ref([])
    const tenders = ref([])
    const totalSubmissions = ref(0)
    const totalPages = ref(0)
    const submissionsTable = ref(null)
    let dataTable = null
    
    const currentPage = computed(() => searchParams.page)
    
    const fetchSubmissions = async () => {
      loading.value = true
      try {
        // Clean params: remove empty strings/null to avoid backend validation errors
        const params = {}
        Object.keys(searchParams).forEach(k => {
          const v = searchParams[k]
          if (v !== undefined && v !== null && v !== '') params[k] = v
        })
        const response = await tenderApi.getAllSubmissions(params)
        // backend returns { data: [...], total, page, limit, totalPages }
        const payload = response && response.data ? response.data : response
        if (payload && Array.isArray(payload.data)) {
          submissions.value = payload.data
        } else if (Array.isArray(payload)) {
          submissions.value = payload
        } else if (Array.isArray(response.data)) {
          submissions.value = response.data
        } else {
          submissions.value = []
        }

        totalSubmissions.value = (response && response.total) || (response && response.data && response.data.total) || 0
        totalPages.value = (response && response.totalPages) || (response && response.data && response.data.totalPages) || 0
        
        // Calculate stats
        calculateStats()
      } catch (error) {
        console.error('Error fetching submissions:', error)
        showToast('Error loading submissions', 'error')
      } finally {
        loading.value = false
        setTimeout(initializeDataTable, 50)
      }
    }
    
    const fetchTenders = async () => {
      try {
        const response = await tenderApi.getAllTenders({ limit: 100 })
        // backend returns { data: [...] }
        if (response && Array.isArray(response.data)) {
          tenders.value = response.data
        } else if (response && response.data && Array.isArray(response.data.data)) {
          tenders.value = response.data.data
        } else if (Array.isArray(response)) {
          tenders.value = response
        } else {
          tenders.value = []
        }
      } catch (error) {
        console.error('Error fetching tenders:', error)
      }
    }

    const initializeDataTable = () => {
      try {
        if (!submissionsTable.value) return
        // remove previous delegated handlers to avoid duplicates
        try {
          $(submissionsTable.value).off('click', '.submission-view')
          $(submissionsTable.value).off('click', '.submission-start-review')
          $(submissionsTable.value).off('click', '.submission-shortlist')
          $(submissionsTable.value).off('click', '.submission-reject')
          $(submissionsTable.value).off('click', '.submission-award')
          $(submissionsTable.value).off('click', '.submission-contract')
          $(submissionsTable.value).off('click', '.submission-resubmission')
        } catch (e) { /* ignore */ }

        if (dataTable) { dataTable.destroy(); dataTable = null }
        dataTable = $(submissionsTable.value).DataTable({
          dom: 'Bfrtip',
          responsive: true,
          language: {
            paginate: {
              previous: '<i class="fas fa-chevron-left"></i>',
              next: '<i class="fas fa-chevron-right"></i>',
            },
          },
          buttons: [
            'colvis',
            'pageLength',
            { extend: 'csv', text: 'Export CSV', className: 'btn btn-primary btn-sm' },
            { extend: 'excel', text: 'Export Excel', className: 'btn btn-primary btn-sm' },
            { extend: 'print', text: 'Print', className: 'btn btn-secondary btn-sm' },
          ],
        })

        // delegated event handlers (DataTables may replace DOM nodes, so use delegation)
        const $table = $(submissionsTable.value)
        $table.on('click', '.submission-view', function (e) {
          const id = $(this).data('id')
          const sub = submissions.value.find(s => String(s.id) === String(id))
          if (sub) viewSubmission(sub)
        })
        $table.on('click', '.submission-start-review', function (e) {
          const id = $(this).data('id')
          updateStatus(id, 'UNDER_REVIEW')
        })
        $table.on('click', '.submission-shortlist', function (e) {
          const id = $(this).data('id')
          updateStatus(id, 'SHORTLISTED')
        })
        $table.on('click', '.submission-reject', function (e) {
          const id = $(this).data('id')
          confirmReject(id)
        })
        $table.on('click', '.submission-award', function (e) {
          const id = $(this).data('id')
          const sub = submissions.value.find(s => String(s.id) === String(id))
          if (sub) openAwardModal(sub)
        })
        $table.on('click', '.submission-contract', function (e) {
          const id = $(this).data('id')
          const sub = submissions.value.find(s => String(s.id) === String(id))
          if (sub) openContractView(sub)
        })
        $table.on('click', '.submission-resubmission', function (e) {
          const id = $(this).data('id')
          const sub = submissions.value.find(s => String(s.id) === String(id))
          if (sub) allowResubmission(sub)
        })
      } catch (err) {
        console.warn('DataTable init failed', err)
      }
    }
    
    const calculateStats = () => {
      stats.total = submissions.value.length
      stats.submitted = submissions.value.filter(s => s.status === 'SUBMITTED').length
      stats.underReview = submissions.value.filter(s => s.status === 'UNDER_REVIEW').length
      stats.shortlisted = submissions.value.filter(s => s.status === 'SHORTLISTED').length
      stats.rejected = submissions.value.filter(s => s.status === 'REJECTED').length
      stats.awarded = submissions.value.filter(s => s.status === 'AWARDED').length
    }
    
    const debouncedSearch = debounce(() => {
      searchParams.page = 1
      fetchSubmissions()
    }, 500)
    
    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        searchParams.page = page
        fetchSubmissions()
      }
    }
    
    const viewSubmission = (submission) => {
      if (!submission || !submission.tender?.id) return
      selectedSubmission.value = submission
      router.push({
        path: `/admin/tenders/${submission.tender.id}/submissions`,
        query: { submissionId: String(submission.id) }
      })
    }
    
    const viewSubmissionDetail = (submission) => {
      router.push(`/admin/tenders/${submission.tender.id}/submissions`)
    }
    
    const goToFullSubmission = () => {
      if (selectedSubmission.value) {
        router.push(`/admin/tenders/${selectedSubmission.value.tender.id}/submissions`)
      }
    }
    
    const quickStatusUpdate = async (submission, status) => {
      try {
        await tenderApi.updateSubmissionStatus(submission.id, { status })
        showToast(`Application ${status.toLowerCase().replace('_', ' ')}`, 'success')
        fetchSubmissions()
      } catch (error) {
        console.error('Error updating status:', error)
        showToast('Error updating application status', 'error')
      }
    }
    
    const formatDate = (dateString) => {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    }
    
    const formatCurrency = (amount, currency = 'KES') => {
      if (!amount && amount !== 0) return 'N/A'
      try {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: currency || 'KES',
          currencyDisplay: 'code'
        }).format(amount)
      } catch (e) {
        return amount;
      }
    }

    const formatStatus = (status) => {
      if (!status) return ''
      return String(status)
        .toLowerCase()
        .replace(/_/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase())
    }

    const getStatusClass = (status) => {
      const statusMap = {
        'SUBMITTED': 'primary',
        'UNDER_REVIEW': 'info', 
        'SHORTLISTED': 'success',
        'REJECTED': 'danger',
        'AWARDED': 'success'
      }
      return statusMap[status] || 'secondary'
    }

    const getContractStatusLabel = (contract) => {
      if (!contract || !contract.endDate) return '—'
      const endDate = new Date(contract.endDate)
      const now = new Date()
      if (endDate < now || (contract.status && String(contract.status).toUpperCase() === 'EXPIRED')) return 'Expired'
      if (contract.status && String(contract.status).toUpperCase() === 'RENEWED') return 'Renewed'
      return 'Active'
    }

    const getContractStatusClass = (contract) => {
      const label = getContractStatusLabel(contract)
      if (label === 'Expired') return 'danger'
      if (label === 'Active') return 'success'
      return 'secondary'
    }

    const clearFilters = () => {
      searchParams.search = ''
      searchParams.status = ''
      searchParams.tenderId = ''
      searchParams.page = 1
      fetchSubmissions()
    }

    const token = (() => { try { return JSON.parse(localStorage.getItem('access_token')) } catch(e) { return localStorage.getItem('access_token') } })()
    const API_BASE = API_BASE_URL

    const updateStatus = async (submissionId, newStatus) => {
      try {
        await tenderApi.updateSubmissionStatus(submissionId, { status: newStatus })
        showToast('Submission status updated successfully', 'success')
        fetchSubmissions()
        calculateStats()
      } catch (error) {
        showToast('Error updating submission status', 'error')
        console.error('Error updating status:', error)
      }
    }

    const allowResubmission = async (submission) => {
      if (!submission?.id) return
      const result = await Swal.fire({
        title: 'Allow resubmission?',
        text: `Enable one re-submission for ${submission.companyName}?`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Allow',
      })
      if (!result.isConfirmed) return

      try {
        await tenderApi.allowSubmissionResubmission(submission.id, true)
        showToast('Resubmission enabled', 'success')
        fetchSubmissions()
      } catch (error) {
        console.error('Error enabling resubmission:', error)
        showToast(error?.message || 'Failed to enable resubmission', 'error')
      }
    }

    const confirmReject = async (submissionId) => {
      const result = await Swal.fire({
        title: 'Reject submission?',
        text: 'Are you sure you want to reject this submission? This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Yes, reject',
      })
      if (result.isConfirmed) {
        await updateStatus(submissionId, 'REJECTED')
      }
    }

    // ── Award Contract ──────────────────────────────────────────────
    const showAwardModal = ref(false)
    const awardingSubmission = ref(null)
    const awarding = ref(false)
    const awardForm = reactive({ awardType: 'normal', contractValue: '', startDate: '', endDate: '', evaluationNotes: '', contractNotes: '' })
    const canSubmitAward = computed(() => {
      if (awardForm.awardType === 'normal') return true
      return awardForm.contractValue && awardForm.startDate && awardForm.endDate
    })

    const openAwardModal = (submission) => {
      awardingSubmission.value = submission
      Object.assign(awardForm, { awardType: 'normal', contractValue: submission.proposedValue || '', startDate: '', endDate: '', evaluationNotes: '', contractNotes: '' })
      showAwardModal.value = true
    }
    const closeAwardModal = () => { showAwardModal.value = false; awardingSubmission.value = null }

    const submitAward = async () => {
      if (!awardingSubmission.value) return
      awarding.value = true
      try {
        const isContract = awardForm.awardType === 'contract'
        const payload = {
          status: 'AWARDED',
          evaluationNotes: awardForm.evaluationNotes || undefined,
          ...(isContract && {
            contractValue: Number(awardForm.contractValue),
            startDate: awardForm.startDate,
            endDate: awardForm.endDate,
            contractNotes: awardForm.contractNotes || undefined,
          }),
        }
        const res = await fetch(`${API_BASE}/tender/submission/${awardingSubmission.value.id}/status`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: token ? `Bearer ${token}` : '' },
          body: JSON.stringify(payload),
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data?.message || 'Failed to award')
        const sub = awardingSubmission.value
        closeAwardModal()
        fetchSubmissions()
        calculateStats()
        if (isContract) {
          router.push({ path: '/tenders/submissions', query: { status: 'AWARDED' } })
        } else if (sub) {
          const supplierId = sub.supplierId || (sub.Supplier && sub.Supplier.id) || null
          const tid = sub.tenderId || (sub.tender && sub.tender.id) || null
          router.push({ path: '/purchases/add', query: { tenderId: String(tid || ''), tenderSubmissionId: String(sub.id), supplierId: String(supplierId) } })
        }
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: err?.message || 'Award failed' })
      } finally {
        awarding.value = false
      }
    }

    // ── Contract View / Generate PO ─────────────────────────────────
    const showContractModal = ref(false)
    const currentContract = ref(null)
    const loadingContract = ref(false)
    const contractSubmissionId = ref(null)

    const openContractView = async (submission) => {
      showContractModal.value = true
      loadingContract.value = true
      currentContract.value = null
      contractSubmissionId.value = submission.id
      try {
        // find contract by submission id via the tenderContract endpoint
        // We'll look up submission detail which should include tenderContract
        const res = await fetch(`${API_BASE}/tender/submission/${submission.id}`, {
          headers: { Authorization: token ? `Bearer ${token}` : '' }
        })
        const data = await res.json()
        const sub = data?.data || data
        if (sub?.tenderContract) {
          const cRes = await fetch(`${API_BASE}/tender/contract/${sub.tenderContract.id}`, {
            headers: { Authorization: token ? `Bearer ${token}` : '' }
          })
          const cData = await cRes.json()
          currentContract.value = cData?.data || cData
        } else {
          currentContract.value = null
        }
      } catch (e) {
        currentContract.value = null
      } finally {
        loadingContract.value = false
      }
    }

    const closeContractModal = () => { showContractModal.value = false; currentContract.value = null }

    const sortedContractPurchases = computed(() => {
      const list = currentContract.value?.purchases || []
      return [...list].sort((a, b) => {
        const da = new Date(a.createdAt || a.id || 0).getTime()
        const db = new Date(b.createdAt || b.id || 0).getTime()
        return db - da
      })
    })

    const refreshContract = async () => {
      if (!currentContract.value) return
      const cRes = await fetch(`${API_BASE}/tender/contract/${currentContract.value.id}`, {
        headers: { Authorization: token ? `Bearer ${token}` : '' }
      })
      const cData = await cRes.json()
      currentContract.value = cData?.data || cData
    }

    const generatePo = () => {
      if (!currentContract.value) return
      const tid = currentContract.value?.tenderId ?? currentContract.value?.tender?.id
      const subId = currentContract.value?.submissionId ?? currentContract.value?.submission?.id
      const supplierId = currentContract.value?.supplierId ?? currentContract.value?.supplier?.id
      closeContractModal()
      router.push({
        path: '/purchases/add',
        query: {
          ...(tid ? { tenderId: String(tid) } : {}),
          ...(subId ? { tenderSubmissionId: String(subId) } : {}),
          ...(supplierId ? { supplierId: String(supplierId) } : {}),
        },
      })
    }

    // ── Extend Contract ─────────────────────────────────────────────
    const showExtendModal = ref(false)
    const savingExtend   = ref(false)
    const extendForm     = reactive({ endDate: '', contractValue: '', notes: '' })
    const minExtendDate  = computed(() => {
      if (!currentContract.value) return ''
      const d = new Date(currentContract.value.endDate)
      d.setDate(d.getDate() + 1)
      return d.toISOString().split('T')[0]
    })

    const openExtendModal = () => {
      extendForm.endDate       = ''
      extendForm.contractValue = ''
      extendForm.notes         = ''
      showExtendModal.value    = true
    }
    const closeExtendModal = () => { showExtendModal.value = false }

    const submitExtend = async () => {
      if (!extendForm.endDate) { Swal.fire({ icon: 'warning', title: 'Required', text: 'Please select a new end date.' }); return }
      savingExtend.value = true
      try {
        const payload = { endDate: extendForm.endDate }
        if (extendForm.contractValue) payload.contractValue = parseFloat(extendForm.contractValue)
        if (extendForm.notes)         payload.notes         = extendForm.notes
        const res = await fetch(`${API_BASE}/tender/contract/${currentContract.value.id}/extend`, {
          method: 'PUT',
          headers: { Authorization: token ? `Bearer ${token}` : '', 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data?.message || 'Failed to extend contract')
        Swal.fire({ icon: 'success', title: 'Success', text: 'Contract extended successfully!' })
        closeExtendModal()
        await refreshContract()
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: err?.message || 'Failed to extend contract' })
      } finally {
        savingExtend.value = false
      }
    }

    // ── Renew Contract ──────────────────────────────────────────────
    const showRenewModal = ref(false)
    const savingRenew    = ref(false)
    const renewForm      = reactive({ startDate: '', endDate: '', contractValue: '', renewalNotes: '' })

    const openRenewModal = () => {
      renewForm.startDate    = ''
      renewForm.endDate      = ''
      renewForm.contractValue = ''
      renewForm.renewalNotes  = ''
      showRenewModal.value   = true
    }
    const closeRenewModal = () => { showRenewModal.value = false }

    const submitRenew = async () => {
      if (!renewForm.startDate || !renewForm.endDate) { Swal.fire({ icon: 'warning', title: 'Required', text: 'Please fill in start and end dates.' }); return }
      savingRenew.value = true
      try {
        const payload = { startDate: renewForm.startDate, endDate: renewForm.endDate }
        if (renewForm.contractValue) payload.contractValue = parseFloat(renewForm.contractValue)
        if (renewForm.renewalNotes)  payload.renewalNotes  = renewForm.renewalNotes
        const res = await fetch(`${API_BASE}/tender/contract/${currentContract.value.id}/renew`, {
          method: 'POST',
          headers: { Authorization: token ? `Bearer ${token}` : '', 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data?.message || 'Failed to renew contract')
        Swal.fire({ icon: 'success', title: 'Success', text: `Contract renewed successfully! New contract #${data.id} created.` })
        closeRenewModal()
        // Load the new contract into the view
        const cRes = await fetch(`${API_BASE}/tender/contract/${data.id}`, {
          headers: { Authorization: token ? `Bearer ${token}` : '' }
        })
        const cData = await cRes.json()
        currentContract.value = cData?.data || cData
      } catch (err) {
        Swal.fire({ icon: 'error', title: 'Error', text: err?.message || 'Failed to renew contract' })
      } finally {
        savingRenew.value = false
      }
    }
    // ────────────────────────────────────────────────────────────────

    const lastRouteStatus = ref(undefined)

    watch(
      () => route.query.status,
      (newStatus) => {
        const normalized = newStatus ? String(newStatus) : ''
        if (normalized === lastRouteStatus.value) return
        lastRouteStatus.value = normalized
        searchParams.status = normalized
        searchParams.page = 1
        fetchSubmissions()
      },
    )

    onMounted(() => {
      if (route.query?.status) searchParams.status = String(route.query.status)
      lastRouteStatus.value = searchParams.status || ''
      fetchTenders()
      fetchSubmissions()
    })

    onBeforeUnmount(() => {
      try { if (dataTable) { dataTable.destroy(); dataTable = null } } catch (e) { /* ignore */ }
      try {
        if (submissionsTable.value) {
          $(submissionsTable.value).off('click', '.submission-view')
          $(submissionsTable.value).off('click', '.submission-start-review')
          $(submissionsTable.value).off('click', '.submission-shortlist')
          $(submissionsTable.value).off('click', '.submission-reject')
          $(submissionsTable.value).off('click', '.submission-award')
          $(submissionsTable.value).off('click', '.submission-contract')
          $(submissionsTable.value).off('click', '.submission-resubmission')
        }
      } catch (e) { /* ignore */ }
    })
    
    return {
      loading,
      selectedSubmission,
      stats,
      searchParams,
      submissions,
      tenders,
      totalSubmissions,
      totalPages,
      currentPage,
      debouncedSearch,
      goToPage,
      viewSubmission,
      viewSubmissionDetail,
      goToFullSubmission,
      quickStatusUpdate,
      formatDate,
      formatCurrency,
      formatStatus,
      getStatusClass,
      getContractStatusLabel,
      getContractStatusClass,
      clearFilters,
      updateStatus,
      allowResubmission,
      confirmReject,
      submissionsTable,
      // award
      showAwardModal,
      awardingSubmission,
      awarding,
      awardForm,
      canSubmitAward,
      openAwardModal,
      closeAwardModal,
      submitAward,
      // contract view
      showContractModal,
      currentContract,
      loadingContract,
      sortedContractPurchases,
      openContractView,
      closeContractModal,
      generatePo,
      // extend contract
      showExtendModal,
      savingExtend,
      extendForm,
      minExtendDate,
      openExtendModal,
      closeExtendModal,
      submitExtend,
      // renew contract
      showRenewModal,
      savingRenew,
      renewForm,
      openRenewModal,
      closeRenewModal,
      submitRenew,
    }
  },
}
</script>

<style scoped>
/* Reuse styles from HomeTransactions.vue */
.transaction {
  min-height: 100vh;
  background-color: #f8f9fa;
}

#transaction-products {
  padding: 20px 0;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Stats Cards */
.warehouse-inventory-card-wrapper {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 20px;
  margin: 30px 0;
}

.warehouse-inventory-card {
  background: white;
  border-radius: 11px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.warehouse-inventory-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 13px rgba(0, 0, 0, 0.15);
}

.warehouse-inventory-card p {
  margin: 0 0 11px 0;
  color: #666;
  font-size: 13px;
  line-height: 1.4;
}

.warehouse-inventory-card strong {
  font-size: 28px;
  font-weight: 700;
  color: #333;
}

.warehouse-inventory-card strong.green {
  color: #28a745;
}

.warehouse-inventory-card strong.red {
  color: #dc3545;
}



.filter-search-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
  border-radius: 11px 11px 0 0;
}

.filter-search-header strong {
  font-size: 18px;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-actions {
  display: flex;
  gap: 11px;
  align-items: center;
}

.clear {
  color: #6b7280;
  cursor: pointer;
  font-size: 13px;
  text-decoration: none;
  border: none;
  background: none;
  padding: 0;
}

.clear:hover {
  color: #374151;
  text-decoration: underline;
}

.filter-search-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 15px;
}

.form-group label {
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
  color: #374151;
  font-size: 13px;
}

.form-control {
  width: 100%;
  padding: 8px 11px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 13px;
}

.form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

select.full {
  width: 100%;
  padding: 8px 11px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 13px;
}

/* Products List */
.products-list {
  background: white;
  border-radius: 11px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 24px;
}

.products-list strong {
  font-size: 18px;
  color: #1f2937;
  margin-bottom: 20px;
  display: block;
}

.table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}

.table th {
  background-color: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
  padding: 15px 11px;
  font-weight: 600;
  font-size: 11px;
  color: #495057;
  text-align: left;
}

.table td {
  padding: 11px;
  border-bottom: 1px solid #dee2e6;
  font-size: 13px;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

/* Reduce spacing for DataTables rows to make rows more compact */
:deep(.table) th,
:deep(.table) td {
  padding: 8px 10px !important;
}

:deep(.dt-buttons) {
  margin-bottom: 0.5rem;
}

:deep(.table thead th) {
  vertical-align: middle;
}

/* Buttons */
.btn {
  padding: 8px 13px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  text-decoration: none;
}

.btn-primary {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.btn-primary:hover {
  background: #2563eb;
  border-color: #2563eb;
}

.btn-sm {
  padding: 4px 8px;
  font-size: 11px;
}

.btn-small {
  padding: 6px 11px;
  font-size: 11px;
}

/* Badges - Contract Status and Status: capitalize */
.badge {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  text-transform: capitalize;
}

.badge-primary { background: #dbeafe; color: #1d4ed8; }
.badge-info { background: #dbeafe; color: #0284c7; }
.badge-success { background: #dcfce7; color: #16a34a; }
.badge-danger { background: #fee2e2; color: #dc2626; }
.badge-secondary { background: #f1f5f9; color: #64748b; }

/* Responsive */
@media (max-width: 768px) {
  .warehouse-inventory-card-wrapper {
    grid-template-columns: repeat(2, 1fr);
    gap: 15px;
  }
  
  .filter-search-header {
    flex-direction: column;
    gap: 11px;
    align-items: flex-start;
  }
  
  .header-actions {
    width: 100%;
    justify-content: flex-end;
  }
}

@media (max-width: 480px) {
  .warehouse-inventory-card-wrapper {
    grid-template-columns: 1fr;
  }
}

.header h1 {
  font-size: 28px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 8px 0;
}

.header p {
  color: #666;
  margin: 0;
}

.stats-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.stat-card {
  background: white;
  border-radius: 11px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  gap: 13px;
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.stat-icon.submitted { background: #3b82f6; }
.stat-icon.review { background: #f59e0b; }
.stat-icon.shortlisted { background: #10b981; }
.stat-icon.total { background: #6b7280; }

.stat-content h3 {
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: #1a1a1a;
}

.stat-content p {
  color: #666;
  margin: 0;
  font-size: 13px;
}

.filters-section {
  background: white;
  border-radius: 11px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.filter-controls {
  display: flex;
  gap: 13px;
  align-items: center;
}

.search-input {
  position: relative;
  flex: 1;
}

.search-input i {
  position: absolute;
  left: 13px;
  top: 50%;
  transform: translateY(-50%);
  color: #666;
}

.search-input input {
  width: 100%;
  padding: 11px 13px 11px 44px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
}

.search-input input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

select {
  padding: 11px 13px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  min-width: 160px;
}

.submissions-table {
  background: white;
  border-radius: 11px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.table-header {
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1fr 1fr 1fr 1.5fr;
  gap: 13px;
  padding: 13px 24px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
}

.header-cell {
  font-weight: 600;
  color: #374151;
  font-size: 13px;
}

.table-row {
  display: grid;
  grid-template-columns: 2fr 2fr 1fr 1fr 1fr 1fr 1.5fr;
  gap: 13px;
  padding: 20px 24px;
  border-bottom: 1px solid #f3f4f6;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.table-row:hover {
  background: #f8fafc;
}

.table-cell {
  display: flex;
  align-items: center;
  font-size: 13px;
}

.company-info h4,
.tender-info h4 {
  font-size: 13px;
  font-weight: 600;
  margin: 0 0 2px 0;
  color: #1a1a1a;
}

.company-info p,
.tender-info p {
  font-size: 11px;
  color: #666;
  margin: 0;
}

.status {
  padding: 4px 11px;
  border-radius: 13px;
  font-size: 11px;
  font-weight: 500;
  text-transform: capitalize;
}

.status.submitted { background: #e0f2fe; color: #0288d1; }
.status.under_review { background: #fff3e0; color: #f57c00; }
.status.shortlisted { background: #e8f5e8; color: #2e7d32; }
.status.rejected { background: #ffebee; color: #d32f2f; }
.status.awarded { background: #f3e5f5; color: #7b1fa2; }

.score {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #f59e0b;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.btn {
  padding: 6px 11px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1px solid transparent;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.btn-sm {
  padding: 4px 8px;
  font-size: 11px;
}

.btn-primary {
  background: #3b82f6;
  color: white;
}

.btn-primary:hover {
  background: #2563eb;
}

.btn-outline {
  border: 1px solid #e5e7eb;
  color: #374151;
  background: white;
}

.btn-outline:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.text-muted {
  color: #9ca3af;
}

.loading {
  text-align: center;
  padding: 48px;
  color: #666;
}

.loading i {
  font-size: 24px;
  margin-bottom: 13px;
}

.empty-state {
  text-align: center;
  padding: 48px;
  color: #666;
}

.empty-state i {
  font-size: 48px;
  margin-bottom: 13px;
  color: #d1d5db;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 13px;
  margin-top: 24px;
}

.page-info {
  color: #666;
  font-size: 13px;
}

/* Modal styles */
@keyframes modal-overlay-in {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes modal-content-in {
  from {
    opacity: 0;
    transform: scale(0.97) translateY(-8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px;
  animation: modal-overlay-in 0.2s ease-out;
}

.modal-content {
  background: white;
  border-radius: 11px;
  max-width: 500px;
  width: 100%;
  max-height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: modal-content-in 0.25s ease-out;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
}

.modal-header h2 {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  color: #1a1a1a;
}

.close-btn {
  background: none;
  border: none;
  font-size: 20px;
  color: #666;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #1a1a1a;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

/* Contract Details Modal - professional styling */
.contract-details-modal {
  max-width: 720px !important;
}
.contract-details-modal .modal-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #111827;
}
.contract-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 40px;
  color: #6b7280;
  font-size: 13px;
}
.contract-loading i {
  font-size: 20px;
}
.contract-details-body {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.contract-info-card {
  background: #f8fafc;
  border-radius: 8px;
  padding: 20px;
  border: 1px solid #e5e7eb;
}
.contract-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 24px;
}
.contract-info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.contract-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b7280;
}
.contract-value {
  font-size: 13px;
  color: #111827;
}
.contract-value-emphasis {
  font-weight: 600;
  color: #0d9488;
}
.contract-status-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  width: fit-content;
  text-transform: capitalize;
}
.contract-status-badge.active {
  background: rgba(34, 197, 94, 0.12);
  color: #16a34a;
}
.contract-status-badge.renewed {
  background: rgba(245, 158, 11, 0.12);
  color: #d97706;
}
.contract-status-badge.terminated,
.contract-status-badge.expired {
  background: rgba(239, 68, 68, 0.12);
  color: #dc2626;
}
.contract-notes {
  padding: 11px 0;
  border-top: 1px solid #e5e7eb;
}
.contract-notes-text {
  margin: 6px 0 0;
  font-size: 13px;
  color: #374151;
  line-height: 1.5;
}
.contract-section {
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
}
.contract-section-title {
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  margin: 0 0 11px;
}
.contract-table {
  width: 100%;
  font-size: 11px;
  border-collapse: collapse;
}
.contract-table th,
.contract-table td {
  padding: 10px 11px;
  text-align: left;
  border-bottom: 1px solid #f3f4f6;
}
.contract-table th {
  font-weight: 600;
  color: #6b7280;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.contract-table td {
  color: #374151;
}
.contract-badge-sm {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  background: #f3f4f6;
  color: #6b7280;
  text-transform: capitalize;
}
.contract-badge-sm.po-status-approved {
  background: rgba(34, 197, 94, 0.15);
  color: #16a34a;
}
.contract-badge-sm.po-status-active {
  background: rgba(59, 130, 246, 0.15);
  color: #2563eb;
}
.contract-badge-sm.po-status-pending {
  background: rgba(245, 158, 11, 0.15);
  color: #d97706;
}
.contract-badge-sm.po-status-saved,
.contract-badge-sm.po-status-draft {
  background: #f3f4f6;
  color: #6b7280;
}
.contract-badge-sm.po-status-fulfilled {
  background: rgba(34, 197, 94, 0.12);
  color: #15803d;
}
.contract-badge-sm.po-status-cancelled,
.contract-badge-sm.po-status-rejected {
  background: rgba(239, 68, 68, 0.15);
  color: #dc2626;
}
.contract-empty {
  font-size: 11px;
  color: #9ca3af;
  padding: 8px 0;
}
.contract-empty-state {
  text-align: center;
  padding: 32px 24px;
}
.contract-empty-message {
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 8px;
}
.contract-empty-hint {
  font-size: 11px;
  color: #9ca3af;
  display: block;
}
.contract-modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-top: 1px solid #e5e7eb;
  background: #fafafa;
  flex-wrap: wrap;
  gap: 10px;
}
.contract-footer-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.contract-footer-actions .btn-primary {
  background: #0d9488;
  border-color: #0d9488;
}
.contract-footer-actions .btn-primary:hover:not(:disabled) {
  background: #0f766e;
  border-color: #0f766e;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 13px;
  margin-bottom: 24px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.detail-item strong {
  font-size: 13px;
  color: #374151;
  font-weight: 500;
}

.detail-item span {
  font-size: 13px;
  color: #666;
}

.detail-section h3 {
  font-size: 13px;
  font-weight: 600;
  margin: 0 0 11px 0;
  color: #1a1a1a;
}

.detail-section p {
  color: #374151;
  line-height: 1.6;
  margin: 0;
}

.modal-actions {
  display: flex;
  gap: 11px;
  justify-content: flex-end;
  margin-top: 24px;
}

/* Compact the submissions table rows (reduce excess vertical padding) */
#submissionsTable thead th {
  padding: 10px 11px !important;
}

#submissionsTable tbody td {
  padding: 8px 11px !important;
  vertical-align: middle;
}

#submissionsTable tbody td p {
  margin: 0;
  line-height: 1.2;
}

#submissionsTable tbody td small {
  display: block;
  margin-top: 2px;
  line-height: 1.2;
}

@media (max-width: 768px) {
  .filter-controls {
    flex-direction: column;
    gap: 11px;
  }
  
  .table-header,
  .table-row {
    grid-template-columns: 1fr;
    gap: 8px;
  }
  
  .table-header {
    display: none;
  }
  
  .table-row {
    display: flex;
    flex-direction: column;
    padding: 13px;
  }
  
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>